import { createSelector } from '@reduxjs/toolkit'

import { IS_BRAND_PULSZBINGO } from 'src/config/brands'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'

import { getTodayBonus, sortByDate } from './utils'

import type { ReduxState } from 'store/types'

const getBonusesFlow = (state: ReduxState) => state.bonusesFlow

export const getDailyBonusesSelector = createSelector(getBonusesFlow, (bonusesFlow) => bonusesFlow?.dailyBonuses)
export const getManualBonusesSelector = createSelector(getBonusesFlow, (bonusesFlow) => bonusesFlow?.manualBonuses)
export const getInvitationBonusesSelector = createSelector(
  getBonusesFlow,
  (bonusesFlow) => bonusesFlow?.invitationBonuses,
)
export const getDelayedBonusesSelector = createSelector(getBonusesFlow, (bonusesFlow) => bonusesFlow?.delayedBonuses)
export const wheelsSelector = createSelector(getBonusesFlow, (bonusesFlow) => bonusesFlow?.wheels)
export const currentWheelSelector = createSelector(wheelsSelector, (wheels) => wheels?.[0])
export const isWheelSpinningSelector = createSelector(getBonusesFlow, (bonusesFlow) => bonusesFlow?.wheelSpinning)
export const isBonusesLoadingSelector = createSelector(getBonusesFlow, (bonusesFlow) => bonusesFlow?.loading)
export const bonusesLoadedSelector = createSelector(getBonusesFlow, (bonusesFlow) => bonusesFlow?.loaded)
export const isShownRewardDialogSelector = createSelector(
  getBonusesFlow,
  (bonusesFlow) => bonusesFlow?.isRewardDialogShown,
)

export const currentWheelCanvasSelector = createSelector(currentWheelSelector, (wheel) => {
  return wheel?.sections?.map(({ code, label, top }) => ({
    code,
    label,
    top,
  }))
})

export const getManualAndInvitationBonusesSelector = createSelector(
  getManualBonusesSelector,
  getInvitationBonusesSelector,
  (manual, invitation) => {
    if (manual && invitation) {
      return sortByDate([...manual, ...invitation])
    }

    return null
  },
)

export const getSCManualAndInvitationBonusesSelector = createSelector(
  getManualAndInvitationBonusesSelector,
  (bonuses) => bonuses?.filter((bonus) => bonus?.sweepstakeAmount && bonus?.goldAmount != null),
)
export const getGCManualAndInvitationBonusesSelector = createSelector(
  getManualAndInvitationBonusesSelector,
  (bonuses) => bonuses?.filter((bonus) => bonus?.goldAmount && !bonus?.sweepstakeAmount),
)
export const getFiatManualBonusSelector = createSelector(getManualAndInvitationBonusesSelector, (bonuses) =>
  bonuses?.filter((bonus) => bonus?.sweepstakeAmount),
)

export const getOrderedManualAndInvitationBonusesSelector = createSelector(
  getSCManualAndInvitationBonusesSelector,
  getGCManualAndInvitationBonusesSelector,
  sweepstakeEnabledSelector,
  (scBonuses, gcBonuses, sweepstakeEnabled) => [...(sweepstakeEnabled ? (scBonuses ?? []) : []), ...(gcBonuses ?? [])],
)

export const isSCDelayedManualBonusAvailableSelector = createSelector(
  getDelayedBonusesSelector,
  sweepstakeEnabledSelector,
  (bonuses, sweepstakeEnabled) => {
    return Boolean(bonuses?.length && bonuses.find((bonus) => bonus?.sweepstakeAmount) && sweepstakeEnabled)
  },
)

export const isGCDelayedManualBonusAvailableSelector = createSelector(getDelayedBonusesSelector, (bonuses) => {
  return Boolean(bonuses?.length && bonuses.find((bonus) => bonus?.goldAmount && !bonus?.sweepstakeAmount))
})

export const getDelayedBonusesGroupedSelector = createSelector(
  getDelayedBonusesSelector,
  sweepstakeEnabledSelector,
  (bonuses, sweepstakeEnabled) => {
    if (!bonuses) {
      return { sc: [], gc: [] }
    }
    const sc = sweepstakeEnabled ? sortByDate(bonuses.filter((bonus) => (bonus?.sweepstakeAmount ?? 0) > 0)) : []
    const gc = sortByDate(bonuses.filter((bonus) => !bonus?.sweepstakeAmount))

    return { sc, gc }
  },
)

export const getSCDelayedBonusesSelector = createSelector(getDelayedBonusesGroupedSelector, ({ sc }) => sc)

export const getGCDelayedBonusesSelector = createSelector(getDelayedBonusesGroupedSelector, ({ gc }) => gc)

export const getOrderedDelayedBonusesSelector = createSelector(getDelayedBonusesGroupedSelector, ({ sc, gc }) => [
  ...sc,
  ...gc,
])

export const isDailyBonusAvailableSelector = createSelector(
  getDailyBonusesSelector,
  currentWheelSelector,
  (bonuses, wheel) => {
    if (IS_BRAND_PULSZBINGO) {
      return Boolean(wheel?.spins)
    } else {
      return Boolean(getTodayBonus(bonuses))
    }
  },
)

export const isManualBonusAvailableSelector = createSelector(
  getManualAndInvitationBonusesSelector,
  getGCManualAndInvitationBonusesSelector,
  sweepstakeEnabledSelector,
  (bonuses, gcBonuses, sweepstakeEnabled) => {
    return sweepstakeEnabled ? Boolean(bonuses?.length) : Boolean(gcBonuses?.length)
  },
)

export const wheelFreeSpinsClaimStateSelector = createSelector(getBonusesFlow, (bonusesFlow) => {
  return bonusesFlow?.wheelFreeSpinsClaimState
})

export const isFreeSpinsNotificationReceivedSelector = createSelector(
  wheelFreeSpinsClaimStateSelector,
  (claimState) => claimState === 'received',
)

export const isClaimWheelFreeSpinsInProgressSelector = createSelector(
  wheelFreeSpinsClaimStateSelector,
  (claimState) => claimState === 'claiming',
)
